/**
 * LegalPagesPage Component
 *
 * Admin page for managing legal pages content (Privacy Policy & Terms of Service).
 * Features:
 * - Tab navigation between pages
 * - Markdown editor for each page
 * - Live preview
 */

import { useState, useEffect } from 'react';
import { useStore } from '../../store';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

type TabType = 'privacy' | 'terms' | 'about';

export function LegalPagesPage() {
  const token = useStore((state) => state.token);

  // Active tab
  const [activeTab, setActiveTab] = useState<TabType>('privacy');

  // Privacy page state
  const [privacyContent, setPrivacyContent] = useState('');
  const [privacyUpdatedAt, setPrivacyUpdatedAt] = useState('');
  const [isSavingPrivacy, setIsSavingPrivacy] = useState(false);
  const [privacyMessage, setPrivacyMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPrivacyPreview, setShowPrivacyPreview] = useState(false);

  // Terms page state
  const [termsContent, setTermsContent] = useState('');
  const [termsUpdatedAt, setTermsUpdatedAt] = useState('');
  const [isSavingTerms, setIsSavingTerms] = useState(false);
  const [termsMessage, setTermsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showTermsPreview, setShowTermsPreview] = useState(false);

  // About page state
  const [aboutContent, setAboutContent] = useState('');
  const [aboutUpdatedAt, setAboutUpdatedAt] = useState('');
  const [isSavingAbout, setIsSavingAbout] = useState(false);
  const [aboutMessage, setAboutMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showAboutPreview, setShowAboutPreview] = useState(false);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // Load both pages on mount
  useEffect(() => {
    loadAllLegalPages();
  }, []);

  const loadAllLegalPages = async () => {
    setIsLoading(true);
    try {
      const [privacyRes, termsRes, aboutRes] = await Promise.all([
        fetch('/api/admin/settings/legal/privacy', {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch('/api/admin/settings/legal/terms', {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch('/api/admin/settings/legal/about', {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      const privacyData = await privacyRes.json();
      const termsData = await termsRes.json();
      const aboutData = await aboutRes.json();

      if (privacyData.success) {
        setPrivacyContent(privacyData.data.content);
        setPrivacyUpdatedAt(privacyData.data.updatedAt);
      }
      if (termsData.success) {
        setTermsContent(termsData.data.content);
        setTermsUpdatedAt(termsData.data.updatedAt);
      }
      if (aboutData.success) {
        setAboutContent(aboutData.data.content);
        setAboutUpdatedAt(aboutData.data.updatedAt);
      }
    } catch (error) {
      console.error('Erreur chargement pages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePrivacy = async () => {
    setIsSavingPrivacy(true);
    setPrivacyMessage(null);
    try {
      const response = await fetch('/api/admin/settings/legal/privacy', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ content: privacyContent }),
      });
      const data = await response.json();
      if (data.success) {
        setPrivacyUpdatedAt(data.data.updatedAt);
        setPrivacyMessage({ type: 'success', text: 'Page enregistrée avec succès' });
      } else {
        setPrivacyMessage({ type: 'error', text: data.error?.message || 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      console.error('Erreur sauvegarde page privacy:', error);
      setPrivacyMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setIsSavingPrivacy(false);
    }
  };

  const handleSaveTerms = async () => {
    setIsSavingTerms(true);
    setTermsMessage(null);
    try {
      const response = await fetch('/api/admin/settings/legal/terms', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ content: termsContent }),
      });
      const data = await response.json();
      if (data.success) {
        setTermsUpdatedAt(data.data.updatedAt);
        setTermsMessage({ type: 'success', text: 'Page enregistrée avec succès' });
      } else {
        setTermsMessage({ type: 'error', text: data.error?.message || 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      console.error('Erreur sauvegarde page terms:', error);
      setTermsMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setIsSavingTerms(false);
    }
  };

  const handleSaveAbout = async () => {
    setIsSavingAbout(true);
    setAboutMessage(null);
    try {
      const response = await fetch('/api/admin/settings/legal/about', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ content: aboutContent }),
      });
      const data = await response.json();
      if (data.success) {
        setAboutUpdatedAt(data.data.updatedAt);
        setAboutMessage({ type: 'success', text: 'Page enregistrée avec succès' });
      } else {
        setAboutMessage({ type: 'error', text: data.error?.message || 'Erreur lors de la sauvegarde' });
      }
    } catch (error) {
      console.error('Erreur sauvegarde page about:', error);
      setAboutMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setIsSavingAbout(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="w-8 h-8 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span className="ml-3 text-slate-600">Chargement des pages légales...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-slate-900">Pages du site</h1>
        <p className="text-slate-500 text-sm mt-1">
          Gérez le contenu des pages "Politique de confidentialité" et "Conditions d'utilisation"
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex border-b border-slate-200">
          <button
            type="button"
            onClick={() => setActiveTab('privacy')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'privacy'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <span>🔒</span>
              Politique de confidentialité
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('terms')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'terms'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <span>📋</span>
              Conditions d'utilisation
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('about')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'about'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <span>ℹ️</span>
              À propos
            </span>
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        {/* Markdown help banner */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm text-blue-800 font-medium mb-2">📝 Aide Markdown</p>
          <p className="text-xs text-blue-700 font-mono">
            **gras** • *italique* • ## Titre • ### Sous-titre • - liste • [texte du lien](url)
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Saut de ligne : ligne vide = nouveau paragraphe • Entrée simple = retour à la ligne
          </p>
        </div>

        {/* Privacy Policy Tab */}
        {activeTab === 'privacy' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-lg text-slate-900">
                Politique de confidentialité
              </h2>
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                Voir la page
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            {/* Privacy message */}
            {privacyMessage && (
              <div
                className={`mb-4 px-4 py-3 rounded-lg flex items-center gap-2 ${
                  privacyMessage.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {privacyMessage.type === 'success' ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  )}
                </svg>
                <span className="text-sm">{privacyMessage.text}</span>
              </div>
            )}

            {/* Updated at */}
            {privacyUpdatedAt && (
              <p className="text-xs text-slate-500 mb-3">
                Dernière modification : {formatDate(privacyUpdatedAt)}
              </p>
            )}

            {/* Preview toggle */}
            <div className="flex items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() => setShowPrivacyPreview(false)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  !showPrivacyPreview
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                }`}
              >
                Édition
              </button>
              <button
                type="button"
                onClick={() => setShowPrivacyPreview(true)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  showPrivacyPreview
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                }`}
              >
                Aperçu
              </button>
            </div>

            {/* Content editor or preview */}
            {showPrivacyPreview ? (
              <div className="h-96 overflow-y-auto border border-slate-200 rounded-lg p-4 bg-white">
                <div className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-6 prose-h2:mb-2 prose-p:text-slate-600 prose-p:leading-relaxed prose-ul:text-slate-600 prose-li:my-0.5 prose-strong:text-slate-700 prose-a:text-primary-600">
                  <ReactMarkdown remarkPlugins={[remarkBreaks]}>{privacyContent}</ReactMarkdown>
                </div>
              </div>
            ) : (
              <textarea
                value={privacyContent}
                onChange={(e) => setPrivacyContent(e.target.value)}
                className="w-full h-96 px-4 py-3 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                placeholder="Contenu en Markdown..."
              />
            )}

            {/* Save button */}
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={handleSavePrivacy}
                disabled={isSavingPrivacy}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingPrivacy ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Enregistrement...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Enregistrer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Terms of Service Tab */}
        {activeTab === 'terms' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-lg text-slate-900">
                Conditions d'utilisation
              </h2>
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                Voir la page
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            {/* Terms message */}
            {termsMessage && (
              <div
                className={`mb-4 px-4 py-3 rounded-lg flex items-center gap-2 ${
                  termsMessage.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {termsMessage.type === 'success' ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  )}
                </svg>
                <span className="text-sm">{termsMessage.text}</span>
              </div>
            )}

            {/* Updated at */}
            {termsUpdatedAt && (
              <p className="text-xs text-slate-500 mb-3">
                Dernière modification : {formatDate(termsUpdatedAt)}
              </p>
            )}

            {/* Preview toggle */}
            <div className="flex items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() => setShowTermsPreview(false)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  !showTermsPreview
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                }`}
              >
                Édition
              </button>
              <button
                type="button"
                onClick={() => setShowTermsPreview(true)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  showTermsPreview
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                }`}
              >
                Aperçu
              </button>
            </div>

            {/* Content editor or preview */}
            {showTermsPreview ? (
              <div className="h-96 overflow-y-auto border border-slate-200 rounded-lg p-4 bg-white">
                <div className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-6 prose-h2:mb-2 prose-p:text-slate-600 prose-p:leading-relaxed prose-ul:text-slate-600 prose-li:my-0.5 prose-strong:text-slate-700 prose-a:text-primary-600">
                  <ReactMarkdown remarkPlugins={[remarkBreaks]}>{termsContent}</ReactMarkdown>
                </div>
              </div>
            ) : (
              <textarea
                value={termsContent}
                onChange={(e) => setTermsContent(e.target.value)}
                className="w-full h-96 px-4 py-3 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                placeholder="Contenu en Markdown..."
              />
            )}

            {/* Save button */}
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={handleSaveTerms}
                disabled={isSavingTerms}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingTerms ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Enregistrement...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Enregistrer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* About Tab */}
        {activeTab === 'about' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-lg text-slate-900">
                À propos
              </h2>
              <a
                href="/about"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                Voir la page
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            {/* About message */}
            {aboutMessage && (
              <div
                className={`mb-4 px-4 py-3 rounded-lg flex items-center gap-2 ${
                  aboutMessage.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {aboutMessage.type === 'success' ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  )}
                </svg>
                <span className="text-sm">{aboutMessage.text}</span>
              </div>
            )}

            {/* Updated at */}
            {aboutUpdatedAt && (
              <p className="text-xs text-slate-500 mb-3">
                Dernière modification : {formatDate(aboutUpdatedAt)}
              </p>
            )}

            {/* Preview toggle */}
            <div className="flex items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() => setShowAboutPreview(false)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  !showAboutPreview
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                }`}
              >
                Édition
              </button>
              <button
                type="button"
                onClick={() => setShowAboutPreview(true)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  showAboutPreview
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                }`}
              >
                Aperçu
              </button>
            </div>

            {/* Content editor or preview */}
            {showAboutPreview ? (
              <div className="h-96 overflow-y-auto border border-slate-200 rounded-lg p-4 bg-white">
                <div className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-6 prose-h2:mb-2 prose-p:text-slate-600 prose-p:leading-relaxed prose-ul:text-slate-600 prose-li:my-0.5 prose-strong:text-slate-700 prose-a:text-primary-600">
                  <ReactMarkdown remarkPlugins={[remarkBreaks]}>{aboutContent}</ReactMarkdown>
                </div>
              </div>
            ) : (
              <textarea
                value={aboutContent}
                onChange={(e) => setAboutContent(e.target.value)}
                className="w-full h-96 px-4 py-3 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                placeholder="Contenu en Markdown..."
              />
            )}

            {/* Save button */}
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={handleSaveAbout}
                disabled={isSavingAbout}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingAbout ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Enregistrement...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Enregistrer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LegalPagesPage;
